import entrySurface from "./ManufacturingImages/mfg-entry-surface.png";
import entrySurfaceWebp from "./ManufacturingImages/mfg-entry-surface.webp";
import scrapCutter from "./ManufacturingImages/mfg-scrap-cutter.png";
import scrapCutterWebp from "./ManufacturingImages/mfg-scrap-cutter.webp";
import ejectorPins from "./ManufacturingImages/mfg-ejector-pins.png";
import ejectorPinsWebp from "./ManufacturingImages/mfg-ejector-pins.webp";
import tubeRouting from "./ManufacturingImages/mfg-tube-routing.png";
import tubeRoutingWebp from "./ManufacturingImages/mfg-tube-routing.webp";
import harnessRouting from "./ManufacturingImages/mfg-harness-routing.png";
import harnessRoutingWebp from "./ManufacturingImages/mfg-harness-routing.webp";
import flangePattern from "./ManufacturingImages/mfg-flange-pattern.png";
import flangePatternWebp from "./ManufacturingImages/mfg-flange-pattern.webp";
import oringGroove from "./ManufacturingImages/mfg-oring-groove.png";
import oringGrooveWebp from "./ManufacturingImages/mfg-oring-groove.webp";

/**
 * Manufacturing and tooling automation shown on /cadautomation.
 *
 * Marketing copy, deliberately brief. No efficiency percentages or timing
 * figures, and no customer, partner or OEM names — keep it that way.
 *
 * Metal tube routing and hose routing are presented as one capability: they
 * are the same automation applied to two part types, and splitting them would
 * put near-identical text on the page twice.
 */
const manufacturingCapabilities = [
  {
    id: "entry-surface",
    img: entrySurface,
    webp: entrySurfaceWebp,
    alt:
      "Entry surface generation shown across straight, curved and multi-curve trimlines",
    title: "Entry Surface Generation",
    lead: "Entry surfaces built straight from the trimline.",
    body: "Building entry surfaces for mould and tool inserts by hand is repetitive and unforgiving — a wrong offset, an angle that drifts or a corner that does not match means starting again. This takes the dieface and trimline and generates the surface directly.",
    outcome: "Insert surfaces that come out right the first time, on complex multi-curve trimlines as well as simple ones.",
  },
  {
    id: "scrap-cutter",
    img: scrapCutter,
    webp: scrapCutterWebp,
    alt:
      "Four-step construction of an upper scrap cutter surface from the offset entry surface",
    title: "Scrap Cutter Body Automation",
    lead: "Upper scrap cutter geometry without the manual rebuild.",
    body: "Designing the upper scrap cutter surface means repeated surface extraction and rotation steps, all needing precise scissor cutting alignment. Geometry mismatches and clearance errors are easy to introduce and slow to find.",
    outcome: "Consistent cutter geometry with the alignment handled for you.",
  },
  {
    id: "ejector-pins",
    img: ejectorPins,
    webp: ejectorPinsWebp,
    alt:
      "Transparent mould base showing ejector pin placement through the plate stack",
    title: "Ejector Pin Placement",
    lead: "Pin layout driven by the geometry, not by hand.",
    body: "Placing ejector pins manually risks uneven ejection, part bending and sticking, and interference that only shows up in production. This detects the flat, safe ejection areas, avoids ribs, bosses and cosmetic surfaces, and distributes the pins across them.",
    outcome: "Balanced ejection and fewer surprises once the tool is cut.",
  },
  {
    id: "tube-routing",
    img: tubeRouting,
    webp: tubeRoutingWebp,
    alt:
      "Metal tube route through an assembly with start, end and support points and a corrugated section marked",
    title: "Tube and Hose Routing",
    lead: "Routing through crowded assemblies, automatically.",
    body: "Routing metal tubes and hoses means picking start, end and support points, defining diameter, thickness and bend radius, working around neighbouring components, checking for clashes, then placing corrugated sections correctly. Every change means doing it again.",
    outcome: "Routes that respect bend radius and clearance, and can be re-run when the packaging moves.",
  },
  {
    id: "harness-routing",
    img: harnessRouting,
    webp: harnessRoutingWebp,
    alt:
      "Wiring harness route with branch points, supports and clip positions annotated",
    title: "Wiring Harness Routing",
    lead: "Branches, clamps and bend radius handled together.",
    body: "Manual harness routing in a complex assembly is hard to standardise between engineers. Branch management, bend radius limits, clamp spacing and interference checks all interact, and getting one wrong usually means reworking the rest.",
    outcome: "Harness layouts that follow your rules consistently across variants.",
  },
  {
    id: "flange-patterns",
    img: flangePattern,
    webp: flangePatternWebp,
    alt:
      "Vacuum chamber assembly with multiple bolted flange interfaces",
    title: "Flange and Bolt Pattern Automation",
    lead: "Hole patterns, threads and hardware in one step.",
    body: "Semiconductor and vacuum tool design means creating flange bolt hole patterns, counterbores and threads, placing bolts, nuts and washers, holding pitch circle diameter and respecting edge distances — often across a great many holes in a single assembly. It is repetitive work where a wrong pitch or thread is easy to miss.",
    outcome: "Standards-compliant patterns generated as a set rather than hole by hole.",
  },
  {
    id: "oring-groove",
    img: oringGroove,
    webp: oringGrooveWebp,
    alt:
      "Grooved and non-grooved flange compared, with the O-ring gland section dimensioned",
    title: "Parametric O-Ring Groove Design",
    lead: "Gland geometry to standard, every time.",
    body: "O-ring grooves in vacuum flanges need precise width and depth, the correct compression ratio and standard-based gland design. Small dimensional errors leak, and a leak means rework and downtime. This generates the groove parametrically against the standard.",
    outcome: "Sealing geometry you can trust across every flange in the assembly.",
    platforms: ["AS568", "ISO"],
  },
];

export default manufacturingCapabilities;
